import { A } from '@mobily/ts-belt'
import { api } from '@setplex/tria-api'
import {
  attach,
  createEffect,
  createEvent,
  createStore,
  sample,
} from 'effector'
import { and, combineEvents, not } from 'patronum'
import {
  CategoryId,
  CategoryType,
  epgCategoriesModel,
} from '~/entities/epg/categories'
import { epgChannelsModel } from '~/entities/epg/channels'
import { id, T } from '~/shared/helpers'
import {
  INITIAL_CHANNELS_COUNT,
  INITIAL_CHANNELS_PAGE,
  INITIAL_COUNT,
  INITIAL_PAGE,
  LOAD_MORE_COUNT,
} from './index.h'
import { shouldAdd } from './lib'

// load initial categories list
export const load = createEvent<{ category?: number }>()

export const $favoritesReady = createStore(false).reset(load)
export const $othersReady = createStore(false)
export const $ready = and($favoritesReady, $othersReady)

export const loadInitialCategories = createEvent<{ category?: number }>()
export const loadAllCategoryChannels = createEvent()
export const loadFavoritesCategoryChannels = createEvent()
export const loadFeaturedCategoryChannels = createEvent()

// load initial categories list effect
const loadInitialCategoriesFx = createEffect<{ category?: number }, void>(
  async ({ category: id }) => {
    let page = INITIAL_PAGE
    let count = INITIAL_COUNT
    let loaded = 0
    for (;;) {
      const data = await epgCategoriesModel.loadPageFx({ page, count })

      // there is no initial selected category or it is made-up
      if (id == null || id <= 0) break

      // no more categories to load
      if (data.last || !data.content || data.content.length === 0) break

      // found initial category
      if (data.content.findIndex((c) => c.id === id) >= 0) break

      // otherwise continue loading
      loaded += data.content.length
      page = Math.floor(loaded / LOAD_MORE_COUNT)
      count = LOAD_MORE_COUNT
    }
  }
)

// load "All" made-up category channels effect
// * do not use `channelsEntity.loadPageFx` intentionally,
// * so response channels will not end up in the `channelsEntity.$byCategory` store
const loadAllCategoryChannelsFx = attach({
  mapParams: () => ({
    page: INITIAL_CHANNELS_PAGE,
    count: INITIAL_CHANNELS_COUNT,
    categoryId: CategoryId.All,
  }),
  effect: api.tv.getAllTvChannelsByPageAbortableFx,
})

// load "Favorites" made-up category channels effect
// * do not use `channelsEntity.loadPageFx` intentionally,
// * so response channels will not end up in the `channelsEntity.$byCategory` store
const loadFavoritesCategoryChannelsFx = attach({
  mapParams: () => ({
    'page': INITIAL_CHANNELS_PAGE,
    'count': INITIAL_CHANNELS_COUNT,
    'only-favorites': true,
  }),
  effect: api.tv.getAllTvChannelsByPageAbortableFx,
})

// load "Featured" made-up category channels effect
const loadFeaturedCategoryChannelsFx = attach({
  mapParams: () => ({ byCategoryId: CategoryId.Featured }),
  effect: epgChannelsModel.loadFeaturedFx,
})

// prevent double categories loading
sample({
  clock: loadInitialCategories,
  filter: not(loadInitialCategoriesFx.pending),
  target: loadInitialCategoriesFx,
})

// prevent double All category loading
sample({
  clock: loadAllCategoryChannels,
  filter: not(loadAllCategoryChannelsFx.pending),
  target: loadAllCategoryChannelsFx,
})

// prevent double Favorites category loading
sample({
  clock: loadFavoritesCategoryChannels,
  filter: not(loadFavoritesCategoryChannelsFx.pending),
  target: loadFavoritesCategoryChannelsFx,
})

// prevent double Featured category loading
sample({
  clock: loadFeaturedCategoryChannels,
  filter: not(loadFeaturedCategoryChannelsFx.pending),
  target: loadFeaturedCategoryChannelsFx,
})

// load initial categories list
sample({
  clock: load,
  filter: not($othersReady),
  target: [
    loadInitialCategories,
    loadAllCategoryChannels,
    loadFeaturedCategoryChannels,
  ],
})

// set ready flag when all categories are loaded and determined
sample({
  clock: combineEvents({
    events: [
      loadInitialCategoriesFx.finally,
      loadAllCategoryChannelsFx.finally,
      // ! do not wait for featured channels to load
      // ! this request is way too slow, got tired of waiting
      // loadFeaturedCategoryChannelsFx.finally,
    ],
  }),
  fn: T,
  target: $othersReady,
})

// reload favorites each time
sample({
  clock: load,
  target: loadFavoritesCategoryChannels,
})

// set ready flag for favorites channels
sample({
  clock: loadFavoritesCategoryChannelsFx.finally,
  fn: T,
  target: $favoritesReady,
})

// add "All" made-up category, if there are channels in it
sample({
  clock: loadAllCategoryChannelsFx.doneData,
  source: epgCategoriesModel.$madeup,
  filter: (madeup, { content }) => shouldAdd(CategoryType.All, madeup, content),
  fn: (_, { content }) => content.map(id),
  target: epgCategoriesModel.addAllCategory,
})

// add "Favorites" made-up category, if there are channels in it
sample({
  clock: loadFavoritesCategoryChannelsFx.doneData,
  source: epgCategoriesModel.$madeup,
  filter: (madeup, { content }) =>
    shouldAdd(CategoryType.Favorite, madeup, content),
  fn: (_, { content }) => content.map(id),
  target: epgCategoriesModel.addFavoriteCategory,
})

// add "Featured" made-up category, if there are channels in it
sample({
  clock: epgChannelsModel.$byCategory,
  source: epgCategoriesModel.$madeup,
  filter: (madeup, [channels]) =>
    shouldAdd(CategoryType.Featured, madeup, channels.get(CategoryId.Featured)),
  fn: (_, [channels]) => A.map(channels.get(CategoryId.Featured) ?? [], id),
  target: epgCategoriesModel.addFeaturedCategory,
})
